import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Surface, Stat } from "@/components/app/Primitives";
import { ShieldCheck, AlertTriangle, CheckCircle2, Building2, TrendingUp } from "lucide-react";

const STATUS_COLOR = {
    green: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-700", label: "Ready" },
    yellow: { bg: "bg-yellow-50", border: "border-yellow-300", text: "text-yellow-700", label: "At Risk" },
    red: { bg: "bg-red-50", border: "border-red-300", text: "text-red-700", label: "Critical" },
};

export default function ComplianceReadiness() {
    const { companies, activeCompanyId, switchCompany } = useAuth();
    const [data, setData] = useState(null);
    const [all, setAll] = useState([]);
    useEffect(() => {
        api.get("/insights/readiness").then((r) => setData(r.data));
        api.get("/insights/readiness/all").then((r) => setAll(r.data || []));
    }, [activeCompanyId]);

    if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
    const c = STATUS_COLOR[data.status] || STATUS_COLOR.yellow;
    return (
        <div>
            <PageHeader
                title="Compliance Confidence"
                subtitle="Real-time score of how ready your company is for SSS, PhilHealth, Pag-IBIG, and BIR submissions."
            />

            <div className={`surface p-5 mb-3 border-2 ${c.border}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Readiness Score</div>
                        <div className="flex items-baseline gap-3 mt-1">
                            <div className={`font-display text-5xl font-bold ${c.text}`} data-testid="readiness-score">{data.score}<span className="text-xl text-muted-foreground">/100</span></div>
                            <span className={`badge ${data.status === "green" ? "badge-approved" : data.status === "yellow" ? "badge-draft" : "badge-rejected"}`} data-testid={`readiness-status-${data.status}`}>{c.label}</span>
                        </div>
                        <div className="mt-3 w-full max-w-md h-3 bg-secondary rounded-full overflow-hidden">
                            <div className={`h-full ${data.status === "green" ? "bg-emerald-500" : data.status === "yellow" ? "bg-yellow-500" : "bg-red-500"}`} style={{ width: `${data.score}%` }} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Stat label="Errors" value={data.error_count} icon={AlertTriangle} />
                        <Stat label="Warnings" value={data.warning_count} icon={ShieldCheck} />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <Surface className="p-4 lg:col-span-2">
                    <h3 className="font-display font-semibold mb-2">Issues & Warnings</h3>
                    <div className="space-y-2 max-h-[60vh] overflow-auto">
                        {[...(data.issues || []).map((i) => ({ ...i, level: "error" })), ...(data.warnings || []).map((w) => ({ ...w, level: "warning" }))].map((it, i) => (
                            <div key={i} className={`flex items-start gap-2 p-2 border-l-2 ${it.level === "error" ? "border-destructive bg-red-50/30" : "border-yellow-400 bg-yellow-50/30"} rounded-r-md`} data-testid={`issue-${it.code}-${i}`}>
                                {it.level === "error" ? <AlertTriangle className="size-4 text-destructive mt-0.5" /> : <AlertTriangle className="size-4 text-yellow-600 mt-0.5" />}
                                <div className="text-xs">
                                    <div className="font-medium">{it.code.replace(/_/g, " ").toUpperCase()}</div>
                                    <div className="text-muted-foreground">{it.message}</div>
                                </div>
                            </div>
                        ))}
                        {!data.issues?.length && !data.warnings?.length && (
                            <div className="text-sm text-emerald-700 flex items-center gap-2 p-3"><CheckCircle2 className="size-4" /> All compliance checks passed. You're ready to file.</div>
                        )}
                    </div>
                </Surface>

                <Surface className="p-4">
                    <h3 className="font-display font-semibold mb-2">Recommended Fixes</h3>
                    <ul className="space-y-2 text-xs">
                        {(data.recommendations || []).map((r, i) => (
                            <li key={i} className="flex items-start gap-2"><span className="size-1.5 rounded-full bg-primary mt-1.5" /> {r}</li>
                        ))}
                        {!data.recommendations?.length && <li className="text-muted-foreground">No fixes needed. Keep it up.</li>}
                    </ul>
                </Surface>
            </div>

            <Surface className="p-4 mt-3">
                <h3 className="font-display font-semibold mb-2">Company Comparison</h3>
                <table className="w-full dense-table">
                    <thead><tr><th>Company</th><th>Status</th><th className="num">Score</th><th className="num">Errors</th><th className="num">Warnings</th><th className="num">Headcount</th><th></th></tr></thead>
                    <tbody>
                        {all.map((row) => (
                            <tr key={row.company.id} data-testid={`comp-row-${row.company.code}`}>
                                <td className="font-medium flex items-center gap-2"><Building2 className="size-3 text-muted-foreground" /> {row.company.name}</td>
                                <td><span className={`badge ${row.status === "green" ? "badge-approved" : row.status === "yellow" ? "badge-draft" : "badge-rejected"}`}>{row.status}</span></td>
                                <td className="num font-bold">{row.score}</td>
                                <td className="num">{row.error_count}</td>
                                <td className="num">{row.warning_count}</td>
                                <td className="num">{row.headcount}</td>
                                <td>
                                    {row.company.id !== activeCompanyId && (
                                        <button onClick={() => { switchCompany(row.company.id); window.location.reload(); }} className="text-xs text-primary" data-testid={`switch-${row.company.code}`}>Switch →</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Surface>
        </div>
    );
}
